import React, { useCallback, useEffect } from 'react';

import cn from 'classnames';

import { useOrdersRecognitionSidebarController } from '@entities/sidebar/config/orders-recognition-sidebar';
import { ToastManager } from '@features';
import { useNuqsQueryState } from '@shared/hooks';
import {
    Flex,
    Sidebar,
    UiButton,
    Icon,
    Popup,
    Lightbox,
    PreviewFileHeader,
    useLightBoxControl,
} from '@shared/ui';
import { useDeleteOrder } from '@widgets/OrdersRecognition/api/hooks';
import { useCurrentOrderId, useSearchOrdersByQuery } from '@widgets/OrdersRecognition/lib/hooks';
import { usePopupController } from '@widgets/OrdersRecognition/lib/hooks/usePopupController';
import { OrdersRecognitionContextProvider } from '@widgets/OrdersRecognition/model/context';
import {
    ORDERS_HIDE_PROCEEDED,
    ORDERS_RECOGNITION_DATA_ATTR_VALUE,
    ORDERS_TOAST_SCOPE,
} from '@widgets/OrdersRecognition/model/сonsts';

import cls from './OrdersRecognition.module.css';
import { OrdersRecognitionHeader } from './OrdersRecognitionHeader';
import { OrdersRecognitionMain } from './OrdersRecognitionMain';
import { useOrdersRecognition } from '../lib/hooks/useOrdersRecognition';

export const OrdersRecognition = () => {
    const { sidebarRef, setSidebarRef } = useOrdersRecognition();
    const { isOpen, halfClose, openHalf, openFull, toggle } =
        useOrdersRecognitionSidebarController();
    const { currentOrderId, clearCurrentOrderId, changeCurrentOrderId } = useCurrentOrderId();
    const { isOpenPopup, closeDeletePopup, openDeletePopup } = usePopupController();
    const { searchDraft, searchApplied, onChangeSearchDraft, commitSearchDraft } =
        useSearchOrdersByQuery();
    const { mutateAsync: deleteAsync, isPending: isPendingDelete } = useDeleteOrder({
        onSuccessCb: closeDeletePopup,
        searchDraft,
    });

    const { onCloseLightBox, onShowLightBox, files, currentFileId, currentFile } =
        useLightBoxControl();

    const { setState } = useNuqsQueryState(ORDERS_HIDE_PROCEEDED);

    useEffect(() => {
        void setState('false');
    }, [setState]);

    const onDeleteHandler = useCallback(async () => {
        await deleteAsync(currentOrderId);
    }, [currentOrderId, deleteAsync]);

    return (
        <OrdersRecognitionContextProvider
            contextValues={{
                currentOrderId,
                searchDraft,
                searchApplied,
                commitSearchDraft,
                onChangeSearchDraft,
                closeDeletePopup,
                openDeletePopup,
                changeCurrentOrderId,
                clearCurrentOrderId,
                onShowLightBox,
            }}
        >
            <Sidebar
                isOpen={isOpen}
                onClose={toggle}
                rootClassName={cn(halfClose ? cls.halfCloseRoot : '')}
                data-sidebar-name={ORDERS_RECOGNITION_DATA_ATTR_VALUE}
                innerRef={setSidebarRef}
            >
                <Flex
                    direction="column"
                    align="start"
                    className={cls.sidebarContentWrapper}
                    onPointerDownCapture={openFull}
                    onKeyDownCapture={() => {
                        openFull();
                    }}
                    onFocusCapture={openFull}
                    onDragEnterCapture={(e) => {
                        e.preventDefault();
                        openFull();
                    }}
                    onDragOverCapture={(e) => {
                        e.preventDefault();
                        openFull();
                    }}
                >
                    <OrdersRecognitionHeader />
                    <OrdersRecognitionMain openFull={openFull} openHalf={openHalf} />
                </Flex>
                <UiButton
                    variant="icon"
                    startIcon={
                        <Icon
                            iconName="doubleArrows"
                            width={12}
                            height={12}
                            className={cn(cls.halfCloseBtnIcon, halfClose && cls.rotate)}
                        />
                    }
                    onClick={openHalf}
                    className={cls.halfCloseBtn}
                />
                <ToastManager
                    toastPosition="left"
                    strategy="absolute"
                    pushStyles={cls.toastStyles}
                    delay={5000}
                    portalTarget={sidebarRef.current}
                    scope={ORDERS_TOAST_SCOPE}
                    renderToast={({ isToastShow, positionStyles, text, type }) => (
                        <div
                            className={cn(cls.customToastContainer, isToastShow && cls.isOpen)}
                            style={positionStyles}
                        >
                            {type === 'success' && <Icon iconName="isChecked" />}
                            {type === 'error' && <Icon iconName="cross" fill="#EC3030" />}
                            <span className={cls.customToastContent}>{text}</span>
                        </div>
                    )}
                />
            </Sidebar>
            <Popup
                isOpen={isOpenPopup}
                type="delete"
                btnActionCb={onDeleteHandler}
                onHidePopup={closeDeletePopup}
                contentText="Вы уверены, что хотите удалить счет?"
                footerClassName={cls.popupFooter}
                isPending={isPendingDelete}
            />
            {!!currentFileId && currentFile && (
                <Lightbox
                    currentFileId={currentFileId}
                    onClose={onCloseLightBox}
                    files={files}
                    headerSlot={
                        <PreviewFileHeader
                            onClose={onCloseLightBox}
                            fileSrc={currentFile.fileSrc}
                            fileName={currentFile.fileName}
                        />
                    }
                />
            )}
            {/* {inCompanyRoute && ( */}
            {/*    <UiButton */}
            {/*        variant="outlined" */}
            {/*        btnColor="ghost" */}
            {/*        startIcon={<Icon width={12} height={12} iconName="document" />} */}
            {/*        btnText="Распознавание счетов" */}
            {/*        className={cls.sidebarTriggerBtn} */}
            {/*        onClick={toggle} */}
            {/*    /> */}
            {/* )} */}

            {/* <button onClick={handleCloseSidebar}>Открыть/закрыть Sidebar!</button> */}
        </OrdersRecognitionContextProvider>
    );
};
